import { supabase } from '../lib/supabase'

/**
 * Addresses already emailed about a diary entry — 29 Sep 2026.
 *
 * Tom, having typed Jack in once already: “when I start writing can it
 * remember past email addresses?”
 *
 * It can, and there is nothing to maintain: the suggestions ARE the history,
 * read back out of `calendar_entries.notify_guests`. Exactly the same
 * principle as the call sheet's known contacts (utils/knownContacts.ts) — the
 * record is a by-product of the work, never a second chore. An address book
 * to keep up to date would be a third place for the same people to live and
 * would be wrong within a month.
 *
 * One honest limit, the same one the call sheet has: this is “addresses on a
 * diary entry that still exists”, not “every address ever typed”. Delete the
 * entry and the suggestion goes with it — which is also how a typo cleans
 * itself up.
 */

export interface KnownGuest {
  email: string
}

/**
 * Most recently used first. Returns an empty list rather than throwing: a
 * form that cannot reach the suggestions is simply a form you type by hand,
 * which is what it was before.
 */
export async function fetchKnownGuestEmails(): Promise<string[]> {
  if (!supabase) return []

  const { data, error } = await supabase.rpc('list_guest_emails')
  if (error) {
    console.warn(`[supabase] list_guest_emails failed: ${error.message}`)
    return []
  }

  return ((data ?? []) as Record<string, unknown>[])
    .map((row) => String(row.email ?? '').trim())
    .filter((email) => email !== '')
}

/**
 * Adds an address to whatever is already typed, comma-separated.
 *
 * Tolerant of how the field was left: a trailing comma, a trailing space, or
 * a half-typed address the person then clicked a suggestion instead of
 * finishing — that fragment is dropped rather than left stranded in front of
 * the address they actually chose.
 */
export function appendGuestEmail(current: string, email: string): string {
  const parts = current
    .split(/[,;]/)
    .map((piece) => piece.trim())
    .filter((piece) => piece !== '' && piece.includes('@'))

  if (parts.some((piece) => piece.toLowerCase() === email.toLowerCase())) {
    return parts.join(', ')
  }
  return [...parts, email].join(', ')
}
